import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/Card';
import { Wheat, TrendingUp, Calendar, Package } from 'lucide-react';
import { cn } from '@/lib/cn';

interface FeedingSummaryProps {
  cycleId: string;
  farmId: string;
  /** Closed cycle: today / this-week figures don't apply. */
  closed?: boolean;
}

interface FeedingSummaryResponse {
  today_kg: number;
  recommended_kg: number | null;
  week_kg: number;
  cycle_total_kg: number;
  current_feed_type: string | null;
}

export function FeedingSummary({ cycleId, farmId, closed = false }: FeedingSummaryProps) {
  const { data, isLoading } = useQuery({
    queryKey: ['feeding-summary', cycleId],
    queryFn: () =>
      api.get<{ data: FeedingSummaryResponse }>(
        `/api/v1/farms/${farmId}/cycles/${cycleId}/feeding/summary`
      ),
  });

  if (isLoading) {
    return (
      <Card>
        <Card.Body className="animate-pulse">
          <div className="h-6 w-24 bg-gray-200 rounded mb-6" />
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-12 bg-gray-100 rounded" />
            ))}
          </div>
        </Card.Body>
      </Card>
    );
  }

  const summary = data?.data;
  const todayKg = summary?.today_kg ? Number(summary.today_kg) : 0;
  const recommendedKg = summary?.recommended_kg ? Number(summary.recommended_kg) : null;
  const weekKg = summary?.week_kg ? Number(summary.week_kg) : 0;
  const cycleTotalKg = summary?.cycle_total_kg ? Number(summary.cycle_total_kg) : 0;
  const feedType = summary?.current_feed_type;

  // Calculate today's progress percentage
  const todayProgress = recommendedKg && recommendedKg > 0
    ? Math.min((todayKg / recommendedKg) * 100, 100)
    : null;

  return (
    <Card>
      <Card.Header>
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-amber-100 rounded-lg">
            <Wheat className="h-4 w-4 text-amber-600" />
          </div>
          Feeding
        </div>
      </Card.Header>

      <Card.Body className="space-y-5">
        {!closed && (
          <>
            {/* Today's Feeding with Progress Bar */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="p-1.5 bg-green-100 rounded">
                    <TrendingUp className="h-3.5 w-3.5 text-green-600" />
                  </div>
                  <span className="text-sm text-gray-500">Today</span>
                </div>
                <span className="text-lg font-bold text-gray-900 tabular-nums">
                  {todayKg.toFixed(1)} kg
                </span>
              </div>

              {todayProgress !== null && (
                <div className="relative">
                  <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className={cn(
                        'h-full rounded-full transition-all duration-500',
                        todayProgress >= 100
                          ? 'bg-gradient-to-r from-green-400 to-green-500'
                          : todayProgress >= 70
                            ? 'bg-gradient-to-r from-amber-400 to-amber-500'
                            : 'bg-gradient-to-r from-gray-300 to-gray-400'
                      )}
                      style={{ width: `${todayProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-400 mt-1">
                    of {recommendedKg?.toFixed(1)} kg recommended
                  </p>
                </div>
              )}

              {todayProgress === null && (
                <p className="text-xs text-gray-400">
                  No recommendation available
                </p>
              )}
            </div>

            {/* Divider */}
            <div className="border-t border-gray-100" />

            {/* Weekly Total */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-1.5 bg-blue-100 rounded">
                  <Calendar className="h-3.5 w-3.5 text-blue-600" />
                </div>
                <span className="text-sm text-gray-500">This Week</span>
              </div>
              <span className="text-lg font-semibold text-gray-900 tabular-nums">
                {weekKg.toFixed(1)} kg
              </span>
            </div>
          </>
        )}

        {/* Cycle Total */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="p-1.5 bg-purple-100 rounded">
              <Package className="h-3.5 w-3.5 text-purple-600" />
            </div>
            <span className="text-sm text-gray-500">Cycle Total</span>
          </div>
          <span className="text-lg font-semibold text-gray-900 tabular-nums">
            {cycleTotalKg.toFixed(0)} kg
          </span>
        </div>

        {/* Current Feed Type */}
        {feedType && (
          <>
            <div className="border-t border-gray-100" />
            <div>
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">
                {closed ? 'Last Feed' : 'Current Feed'}
              </p>
              <p className="font-semibold text-gray-900">{feedType}</p>
            </div>
          </>
        )}
      </Card.Body>
    </Card>
  );
}
